import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { apiFetch, throwIfNotOk } from './services/apiClient';
import './InterviewsPage.css';

const InterviewsPage = () => {
    const [interviews, setInterviews] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [filterStatus, setFilterStatus] = useState('all');
    const [filterType, setFilterType] = useState('all');

    useEffect(() => {
        fetchInterviews();
        
        // Add event listener to refresh interviews when user returns to the page
        const handleFocus = () => {
            fetchInterviews();
        };
        
        // Add event listener for when the page becomes visible again
        const handleVisibilityChange = () => {
            if (!document.hidden) {
                fetchInterviews();
            }
        };
        
        window.addEventListener('focus', handleFocus);
        document.addEventListener('visibilitychange', handleVisibilityChange);
        
        // Cleanup event listeners
        return () => {
            window.removeEventListener('focus', handleFocus);
            document.removeEventListener('visibilitychange', handleVisibilityChange);
        };
    }, []);

    const fetchInterviews = async () => {
        try {
            const response = await apiFetch('/api/interviews');
            await throwIfNotOk(response);
            const data = await response.json();
            console.log('📊 Fetched interviews:', data);
            setInterviews(data);
        } catch (error) {
            setError(error.message);
        } finally {
            setIsLoading(false);
        }
    };

    const handleDeleteInterview = async (interviewId) => {
        if (!window.confirm('Are you sure you want to delete this interview? This action cannot be undone.')) {
            return;
        }

        try {
            const response = await apiFetch(`/api/interviews/${interviewId}`, {
                method: 'DELETE',
            });

            await throwIfNotOk(response);
            
            // Remove the interview from the local state
            setInterviews(interviews.filter(interview => interview.id !== interviewId));
        } catch (error) {
            setError(error.message);
        }
    };

    const getStatusColor = (status) => {
        const normalized = (status || '').toLowerCase();
        switch (normalized) {
            case 'scheduled': return 'status-scheduled';
            case 'completed': return 'status-completed';
            case 'cancelled': return 'status-cancelled';
            case 'rescheduled': return 'status-rescheduled';
            case 'pending_slot_selection':
            case 'pending':
                return 'status-pending';
            default: return 'status-default';
        }
    };

    const formatStatusLabel = (status) => {
        if (!status) return 'Unknown';
        return status
            .split('_')
            .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
            .join(' ');
    };

    const truncateText = (text, maxLen = 56) => {
        if (!text) return '—';
        const trimmed = String(text).trim();
        if (trimmed.length <= maxLen) return trimmed;
        return `${trimmed.slice(0, maxLen)}…`;
    };

    const getTypeIcon = (type) => {
        switch (type) {
            case 'phone': return '📞';
            case 'video': return '📹';
            case 'onsite': return '🏢';
            case 'technical': return '💻';
            default: return '📋';
        }
    };

    const formatDateTime = (dateString) => {
        if (!dateString) return 'Not scheduled';
        const date = new Date(dateString);
        return date.toLocaleString();
    };

    const filteredInterviews = interviews.filter(interview => {
        const statusMatch = filterStatus === 'all' || interview.status === filterStatus;
        const typeMatch = filterType === 'all' || interview.interview_type === filterType;
        return statusMatch && typeMatch;
    });

    if (isLoading) return <div className="interviews-container"><p>Loading interviews...</p></div>;
    if (error) return <div className="interviews-container message error" role="alert">{error}</div>;

    return (
        <div className="interviews-container">
            <div className="glass-container interviews-header">
                <h1>Interview Management</h1>
                <p>Manage and track all your candidate interviews in one place.</p>
            </div>

            <div className="interviews-toolbar glass-container">
                <div className="interviews-filters">
                    <div className="filter-group">
                        <label htmlFor="interview-filter-status">Status</label>
                        <select
                            id="interview-filter-status"
                            value={filterStatus}
                            onChange={(e) => setFilterStatus(e.target.value)}
                        >
                            <option value="all">All</option>
                            <option value="scheduled">Scheduled</option>
                            <option value="completed">Completed</option>
                            <option value="cancelled">Cancelled</option>
                            <option value="rescheduled">Rescheduled</option>
                            <option value="pending_slot_selection">Pending slots</option>
                        </select>
                    </div>
                    <div className="filter-group">
                        <label htmlFor="interview-filter-type">Type</label>
                        <select
                            id="interview-filter-type"
                            value={filterType}
                            onChange={(e) => setFilterType(e.target.value)}
                        >
                            <option value="all">All</option>
                            <option value="phone">Phone</option>
                            <option value="video">Video</option>
                            <option value="onsite">Onsite</option>
                            <option value="technical">Technical</option>
                        </select>
                    </div>
                </div>
                <div className="interviews-stats">
                    <span>{filteredInterviews.length} shown</span>
                    <span className="interviews-stats-sep">·</span>
                    <span>{interviews.length} total</span>
                </div>
            </div>

            {filteredInterviews.length === 0 ? (
                <div className="glass-container empty-state">
                    <h3>No interviews found</h3>
                    <p>Create your first interview by going to a job and scheduling interviews for candidates.</p>
                    <Link to="/jobs" className="cta-button">View Jobs</Link>
                </div>
            ) : (
                <div className="interviews-grid">
                    {filteredInterviews.map(interview => (
                        <div key={interview.id} className="interview-card">
                            <div className="interview-card-top">
                                <div className="interview-card-head">
                                    <span className="interview-type-chip">
                                        <span className="type-icon" aria-hidden="true">
                                            {getTypeIcon(interview.interview_type)}
                                        </span>
                                        <span className="type-text">{interview.interview_type}</span>
                                    </span>
                                    <h3 className="interview-title" title={interview.title}>
                                        {truncateText(interview.title, 42)}
                                    </h3>
                                </div>
                                <span className={`status-badge ${getStatusColor(interview.status)}`}>
                                    {formatStatusLabel(interview.status)}
                                </span>
                                <button
                                    type="button"
                                    className="interview-delete-btn"
                                    onClick={() => handleDeleteInterview(interview.id)}
                                    title="Delete interview"
                                    aria-label="Delete interview"
                                >
                                    🗑️
                                </button>
                            </div>
                            
                            <div className="interview-card-meta">
                                <div className="meta-item">
                                    <span className="meta-label">Candidate</span>
                                    <span className="meta-value" title={interview.candidate_name}>
                                        {truncateText(interview.candidate_name || 'Unknown', 28)}
                                    </span>
                                </div>
                                <div className="meta-item meta-item-wide">
                                    <span className="meta-label">Job</span>
                                    <span className="meta-value" title={interview.job_title}>
                                        {truncateText(interview.job_title, 40)}
                                    </span>
                                </div>
                                <div className="meta-item">
                                    <span className="meta-label">Scheduled</span>
                                    <span className="meta-value">
                                        {formatDateTime(interview.scheduled_at)}
                                    </span>
                                </div>
                                <div className="meta-item">
                                    <span className="meta-label">Duration</span>
                                    <span className="meta-value">{interview.duration_minutes} min</span>
                                </div>
                                {interview.primary_interviewer && (
                                    <div className="meta-item meta-item-wide">
                                        <span className="meta-label">Interviewer</span>
                                        <span className="meta-value" title={interview.primary_interviewer}>
                                            {truncateText(interview.primary_interviewer, 32)}
                                        </span>
                                    </div>
                                )}
                            </div>
                            
                            <div className="interview-card-actions">
                                <Link
                                    to={`/jobs/${interview.job_id}`}
                                    className="interview-btn interview-btn-primary"
                                >
                                    View Job
                                </Link>
                                <Link
                                    to={`/select-slots/${interview.job_id}/${interview.resume_id}`}
                                    className="interview-btn interview-btn-secondary"
                                >
                                    Select Slots
                                </Link>
                                {interview.status === 'completed' && (
                                    <button type="button" className="interview-btn interview-btn-secondary">
                                        View Feedback
                                    </button>
                                )}
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

export default InterviewsPage; 